import { useEffect, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Facebook, Discord } from 'react-bootstrap-icons';

import { LoginModal } from './LoginModal';
import { useAccount } from '../Context/AccountProvider';
import { handleToken, handleLogout } from '../services/AuthService';
import { getToken } from '../services/TokenService';
import { getUserData } from '../services/UserService';
import { useHistory } from 'react-router-dom';
import './NavigationBar.css';

const INFO_ITEMS = [
    { to: '/rules',        label: 'Game Rules' },
    { to: '/tos',          label: 'Terms of Service' },
    { to: '/privacy',      label: 'Privacy Policy' },
    { to: '/refundpolicy', label: 'Refund Policy' },
];

const GUIDE_ITEMS = [
    { to: '/guide',         label: 'Player Guide' },
    { to: '/eventschedule', label: 'Event Schedule' },
    { to: '/faq',           label: 'FAQ' },
];

function Caret({ open }) {
    return (
        <svg
            className={`snav-caret${open ? ' snav-caret--open' : ''}`}
            width="8" height="5" viewBox="0 0 8 5"
            aria-hidden="true"
        >
            <path d="M0 0l4 5 4-5z" fill="currentColor" />
        </svg>
    );
}

// Desktop: hover-triggered dropdown
function HoverDropdown({ title, items, onItemClick, alignRight }) {
    return (
        <div className="snav-drop">
            <button className="snav-link snav-drop-trigger" type="button">
                {title} <Caret />
            </button>
            <div className={`snav-drop-menu${alignRight ? ' snav-drop-menu--right' : ''}`}>
                {items.map(({ to, label }) => (
                    <NavLink key={to} to={to} className="snav-drop-item" activeClassName="active" onClick={onItemClick}>
                        {label}
                    </NavLink>
                ))}
            </div>
        </div>
    );
}

// Mobile: tap-to-expand accordion section
function MobileAccordion({ title, items, onItemClick, gold }) {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <button
                className={`mob-link${gold ? ' mob-link--gold' : ''}`}
                type="button"
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
            >
                {title} <Caret open={open} />
            </button>
            {open && (
                <div className="mob-accordion-body">
                    {items.map(({ to, label }) => (
                        <NavLink key={to} to={to} className="mob-sub-link" activeClassName="active" onClick={onItemClick}>
                            {label}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}

function NavigationBar() {
    const { accountInfo, setAccInfo, updateAccountInfo } = useAccount();
    const [loginShow, setLoginShow] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const history = useHistory();

    // Validate stored token once on mount and hydrate auth state
    useEffect(() => {
        if (getToken() === null) return;
        let cancelled = false;

        async function initAuth() {
            const valid = await handleToken(getToken());
            if (cancelled) return;
            if (!valid) { updateAccountInfo({ loading: false }); return; }
            try {
                const userData = await getUserData();
                if (!cancelled) updateAccountInfo({ loggedIn: true, user: userData, loading: false });
            } catch {
                if (!cancelled) updateAccountInfo({ loading: false });
            }
        }

        initAuth();
        return () => { cancelled = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Close login modal when auth resolves
    useEffect(() => {
        if (loginShow && accountInfo.loggedIn) setLoginShow(false);
    }, [loginShow, accountInfo.loggedIn]);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    const scrollToContent = () =>
        document.getElementById('main-content')?.scrollIntoView({ block: 'start' });

    const closeMenu = () => { setMenuOpen(false); scrollToContent(); };

    const doLogout = () => {
        setAccInfo({ loggedIn: false });
        handleLogout();
        history.push('/');
        setMenuOpen(false);
    };

    const accountItems = [
        ...(accountInfo.user ? [{ to: '/myaccount', label: 'My Account' }] : []),
        { to: '/changepassword', label: 'Change Password' },
        ...(accountInfo.user ? [
            { to: '/buycoins',  label: 'Buy Coins' },
            { to: '/tickets',   label: 'Support Tickets' },
        ] : []),
        ...(accountInfo.user?.isCreator ? [{ to: '/creator', label: 'Creator Dashboard' }] : []),
    ];

    const isStaff = !!accountInfo.user?.serverStaff;

    return (
        <>
            {menuOpen && <div className="snav-backdrop" onClick={() => setMenuOpen(false)} />}

            <nav className="site-nav" aria-label="Main navigation">

                {/* ── Desktop: [left links] [BRAND] [right actions] ── */}
                <div className="snav-desktop">
                    <div className="snav-left">
                        <NavLink exact to="/" className="snav-link" activeClassName="active" onClick={scrollToContent}>Home</NavLink>
                        <NavLink to="/register" className="snav-link" activeClassName="active" onClick={scrollToContent}>Register</NavLink>
                        <NavLink to="/download" className="snav-link" activeClassName="active" onClick={scrollToContent}>Download</NavLink>
                        <HoverDropdown title="Information" items={INFO_ITEMS} onItemClick={scrollToContent} />
                        <HoverDropdown title="Guide" items={GUIDE_ITEMS} onItemClick={scrollToContent} />
                    </div>

                    <Link to="/" className="snav-brand" onClick={scrollToContent}>
                        Paragon Conquer
                    </Link>

                    <div className="snav-right">
                        <a className="snav-icon" rel="noreferrer" href="https://www.facebook.com/Paragon-Conquer-103703422181032" target="_blank" aria-label="Facebook">
                            <Facebook size={16} />
                        </a>
                        <a className="snav-icon" rel="noreferrer" href="https://discord.gg/F7tUnrBMDY" target="_blank" aria-label="Discord">
                            <Discord size={16} />
                        </a>

                        {!accountInfo.loggedIn ? (
                            <button className="snav-login-btn" type="button" onClick={() => setLoginShow(true)}>
                                Login
                            </button>
                        ) : (
                            <div className="snav-drop">
                                <button className="snav-link snav-drop-trigger snav-link--gold" type="button">
                                    {accountInfo.user?.name ?? 'Account'} <Caret />
                                </button>
                                <div className="snav-drop-menu snav-drop-menu--right">
                                    {accountItems.map(({ to, label }) => (
                                        <NavLink key={to} to={to} className="snav-drop-item" activeClassName="active" onClick={scrollToContent}>
                                            {label}
                                        </NavLink>
                                    ))}
                                    {isStaff && (
                                        <>
                                            <div className="snav-drop-divider" />
                                            <NavLink to="/staff" className="snav-drop-item" activeClassName="active" onClick={scrollToContent}>
                                                Staff Panel
                                            </NavLink>
                                        </>
                                    )}
                                    <div className="snav-drop-divider" />
                                    <button className="snav-drop-item" type="button" onClick={doLogout}>
                                        Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Mobile: brand + hamburger ── */}
                <div className="snav-mobile-bar">
                    <Link to="/" className="snav-brand" onClick={closeMenu}>
                        Paragon Conquer
                    </Link>
                    <button
                        className={`snav-hamburger${menuOpen ? ' open' : ''}`}
                        type="button"
                        onClick={() => setMenuOpen(o => !o)}
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={menuOpen}
                    >
                        <span /><span /><span />
                    </button>
                </div>

            </nav>

            {/* ── Mobile: slide-in drawer ── */}
            <div className={`snav-mobile-menu${menuOpen ? ' snav-mobile-menu--open' : ''}`} aria-hidden={!menuOpen}>
                <NavLink exact to="/" className="mob-link" activeClassName="active" onClick={closeMenu}>Home</NavLink>
                <NavLink to="/register" className="mob-link" activeClassName="active" onClick={closeMenu}>Register</NavLink>
                <NavLink to="/download" className="mob-link" activeClassName="active" onClick={closeMenu}>Download</NavLink>
                <MobileAccordion title="Information" items={INFO_ITEMS} onItemClick={closeMenu} />
                <MobileAccordion title="Guide" items={GUIDE_ITEMS} onItemClick={closeMenu} />

                <div className="mob-divider" />

                {!accountInfo.loggedIn ? (
                    <button className="mob-link mob-link--gold" type="button" onClick={() => { setLoginShow(true); setMenuOpen(false); }}>
                        Login
                    </button>
                ) : (
                    <>
                        <MobileAccordion
                            title={accountInfo.user?.name ?? 'Account'}
                            items={accountItems}
                            onItemClick={closeMenu}
                            gold
                        />
                        {isStaff && (
                            <NavLink to="/staff" className="mob-link" activeClassName="active" onClick={closeMenu}>
                                Staff Panel
                            </NavLink>
                        )}
                        <button className="mob-link" type="button" onClick={doLogout}>
                            Logout
                        </button>
                    </>
                )}
            </div>

            <LoginModal
                closeModal={setLoginShow}
                show={loginShow || accountInfo.showLogin}
                onHide={() => { setLoginShow(false); updateAccountInfo({ showLogin: false }); }}
            />
        </>
    );
}

export { NavigationBar };
